/**
 * Topic navigation for the redesigned TopNav.
 *
 * The header groups the org's pages into the four product sections the user
 * sees in the dropdown — Overview, Security Posture, Monitoring, Detection &
 * Response — rather than by permission scope. Each section carries a hue for
 * its identity colour and the filtered list of pages the current user may see
 * (permission + module + role gating reuses the same `visible` list as the
 * sidebar, so the two navs never disagree about access).
 *
 * Separate from `useNav` (which still powers scope-based views) so the topic
 * model here can evolve without touching scope logic.
 */
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { NAV_ITEMS, NAV_TOPICS, type NavItem, type TopicId } from "@app/navigation/nav";
import { useAuth } from "@features/auth/AuthContext";
import { useCan } from "@features/auth/hooks/useCan";
import { useModule } from "@features/auth/hooks/useModule";

/** Scope depth, mirroring useNav — a user sees items at or below their rank. */
const SCOPE_RANK: Record<string, number> = { platform: 3, group: 2, org: 1 };

export type Hue = "blue" | "purple" | "cyan" | "amber";

/** Section identity: which topic maps to which hue in the dropdown. */
const TOPIC_HUE: Partial<Record<TopicId, Hue>> = {
  overview: "blue",
  posture: "purple",
  monitoring: "cyan",
  detection: "amber",
};

/** Topics that appear in the TopNav sections (admin lives in the sidebar). */
const TOPNAV_TOPICS: TopicId[] = ["overview", "posture", "monitoring", "detection"];

export interface TopicSection {
  topic: TopicId;
  label: string;
  icon: string;
  hue: Hue;
  items: NavItem[];
}

export function useTopicNav() {
  const { user } = useAuth();
  const { can } = useCan();
  const { hasModule } = useModule();
  const { pathname } = useLocation();

  const userRank = SCOPE_RANK[user?.scope_level ?? "org"] ?? 1;

  // Same gating the sidebar uses: permission + module + scope. Kept inline
  // rather than imported so a change here can't silently alter the sidebar.
  const visible = useMemo(
    () =>
      NAV_ITEMS.filter((i) => {
        if (i.perm && !can(i.perm)) return false;
        if (i.module && !hasModule(i.module)) return false;
        // A user can't see items pitched above their scope level…
        if (i.scopeLevel && (SCOPE_RANK[i.scopeLevel] ?? 0) > userRank) return false;
        // …and exactScope items don't leak up to broader scopes.
        if (i.exactScope && user?.scope_level !== i.exactScope) return false;
        return true;
      }),
    [can, hasModule, userRank, user?.scope_level],
  );

  // Items whose home is the Browse panel. Excludes anything flagged
  // `sidebar: true` — those live in the left rail and would double-render
  // if listed here too. The crumb lookup below still spans the full
  // `visible` list, so sidebar pages still resolve to their activeItem.
  const browseVisible = useMemo(
    () => visible.filter((i) => !i.sidebar),
    [visible],
  );

  const sections = useMemo<TopicSection[]>(() => {
    return TOPNAV_TOPICS.map((topicId) => {
      const meta = NAV_TOPICS.find((t) => t.id === topicId);
      const items = browseVisible.filter((i) => i.topic === topicId);
      return {
        topic: topicId,
        label: meta?.label ?? topicId,
        icon: meta?.icon ?? "fa-circle",
        hue: TOPIC_HUE[topicId] ?? "blue",
        items,
      };
    }).filter((s) => s.items.length > 0);
  }, [browseVisible]);

  // The active page (if the current route is one of the topic pages). When the
  // user is on a left-sidebar page instead, this is undefined → the crumb shows
  // "Browse".
  const activeItem = useMemo(
    () => visible.find((i) => i.to === pathname || (i.altMatch && new RegExp(i.altMatch).test(pathname))),
    [visible, pathname],
  );

  const activeSection = useMemo(
    () => sections.find((s) => s.items.some((i) => i.id === activeItem?.id)),
    [sections, activeItem],
  );

  return { sections, activeItem, activeSection };
}
